import type {
  SearchParameterFieldType,
  SearchParametersRequest,
} from "@fartherfinance/frontend/api/TaskWorkflows/requests/searchParameters";
import type {
  FieldParameter,
  ResourceType,
} from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import {
  getFieldKey,
  getResourceType,
} from "@fartherfinance/frontend/api/TaskWorkflows/utils/fieldHelpers";
import { hasEnumData } from "@fartherfinance/frontend/api/TaskWorkflows/utils/guards";

import type { UseFormGetValues } from "react-hook-form";

const buildSearchParameterField = (
  resourceType: ResourceType,
  getValues?: UseFormGetValues<Record<string, unknown>>
): SearchParameterFieldType => {
  const baseRequest: SearchParameterFieldType = {
    type: resourceType.type,
    kinds: resourceType.kinds,
    params: {},
  };

  if (!resourceType.params) {
    return baseRequest;
  }

  const resolvedParams = Object.fromEntries(
    Object.entries(resourceType.params)
      .map(([key, value]) => {
        if (!value) {
          return [key, undefined];
        }

        if ("ref" in value && value.ref && getValues) {
          const formValue = getValues(String(value.ref));
          if (
            formValue &&
            typeof formValue === "object" &&
            "value" in formValue
          ) {
            return [key, { ...formValue, value: formValue.value ?? undefined }];
          }
          return [key, formValue];
        }

        return [key, value.value ?? undefined];
      })
      .filter(([, value]) => {
        if (
          value &&
          typeof value === "object" &&
          "value" in value &&
          value.value === undefined
        ) {
          return false;
        }
        return value !== undefined;
      })
  );

  return {
    ...baseRequest,
    params: resolvedParams,
  };
};

export const buildBatchFieldSearchParameters = (
  fieldParameters: FieldParameter[],
  getValues?: UseFormGetValues<Record<string, unknown>>
): SearchParametersRequest["fields"] => {
  const fieldsRequest: SearchParametersRequest["fields"] = {};

  fieldParameters.forEach((field) => {
    if (!hasEnumData(field)) {
      return;
    }

    const key = getFieldKey(field);
    const resourceType = getResourceType(field);

    if (!resourceType) {
      return;
    }

    const fieldRequest = buildSearchParameterField(resourceType, getValues);
    fieldsRequest[key] = fieldRequest;
  });

  return fieldsRequest;
};
